/**
 * Profile API functions
 */

import { api } from './client';

export interface Profile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  job_title?: string;
  role: string;
  organization_id?: string;
  organization_name?: string;
  organization_tier?: string;
  zoho_contact_id?: string;
  mfa_enabled?: boolean;
  notification_prefs?: string;
  created_at: number;
  last_login?: number;
}

export interface UpdateProfileRequest {
  first_name?: string;
  last_name?: string;
  phone?: string;
  job_title?: string;
  notification_prefs?: string;
}

/**
 * Get current user's profile
 */
export async function getProfile(): Promise<Profile> {
  const response = await api.get<{ data: Profile }>('/profile');
  return response.data;
}

/**
 * Update current user's profile
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<Profile> {
  const response = await api.patch<{ data: Profile }>('/profile', data);
  return response.data;
}

// ============================================
// NOTIFICATIONS
// ============================================

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: NotificationType;
  link_type?: string;
  link_id?: string;
  link_url?: string;
  is_read: boolean;
  created_at: number;
}

export interface NotificationsResponse {
  data: Notification[];
  unread_count: number;
}

/**
 * Get current user's notifications
 */
export async function getNotifications(unreadOnly: boolean = false): Promise<NotificationsResponse> {
  const url = unreadOnly ? '/profile/notifications?unread=true' : '/profile/notifications';
  return api.get<NotificationsResponse>(url);
}

/**
 * Mark a notification as read
 */
export async function markNotificationRead(notificationId: string): Promise<void> {
  await api.post(`/profile/notifications/${notificationId}/read`, {});
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsRead(): Promise<void> {
  await api.post('/profile/notifications/read-all', {});
}

/**
 * Delete all notifications
 */
export async function deleteAllNotifications(): Promise<{ count: number }> {
  const response = await api.delete<{ data: { message: string; count: number } }>('/profile/notifications');
  return { count: response.data.count };
}

// ============================================
// MFA (Multi-Factor Authentication)
// ============================================

export interface MfaStatus {
  enabled: boolean;
}

export interface MfaSetupResponse {
  secret: string;
  otpauth_uri: string;
  issuer: string;
}

/**
 * Get current MFA status
 */
export async function getMfaStatus(): Promise<MfaStatus> {
  const response = await api.get<{ data: MfaStatus }>('/mfa/status');
  return response.data;
}

/**
 * Start MFA setup - generates secret and QR code URI
 */
export async function setupMfa(): Promise<MfaSetupResponse> {
  const response = await api.post<{ data: MfaSetupResponse }>('/mfa/setup', {});
  return response.data;
}

/**
 * Verify MFA code and enable MFA
 */
export async function verifyMfa(code: string): Promise<{ message: string; enabled: boolean }> {
  const response = await api.post<{ data: { message: string; enabled: boolean } }>('/mfa/verify', { code });
  return response.data;
}

/**
 * Disable MFA (requires current TOTP code)
 */
export async function disableMfa(code: string): Promise<{ message: string; enabled: boolean }> {
  const response = await api.post<{ data: { message: string; enabled: boolean } }>('/mfa/disable', { code });
  return response.data;
}
